import type { FacebookPixelService, FacebookPixelState } from '../types/facebook';
import { analyticsLogger } from '../utils/analyticsLogger';

const initialState: FacebookPixelState = {
  initialized: false,
  pixelId: undefined,
  lastError: undefined,
  lastEventTimestamp: undefined,
};

let state: FacebookPixelState = { ...initialState };

// Helper para asegurar que fbq existe en window
const getFbq = (): ((...args: any[]) => void) => {
  if (typeof window === 'undefined' || !(window as any).fbq) {
    // En un entorno de test sin un mock, o si el script no se carga,
    // devolvemos una función vacía para evitar errores de ejecución.
    return () => {};
  }
  return (window as any).fbq;
};

export const FacebookPixel: FacebookPixelService = {
  isInitialized: () => state.initialized,

  init: (pixelId: string) => {
    // Validar el formato del Pixel ID (debe ser numérico) ANTES del try...catch
    const pixelIdRegex = /^\d+$/;
    if (!pixelId || !pixelIdRegex.test(pixelId)) {
      throw new Error('Invalid Facebook Pixel ID format');
    }

    try {
      if (state.initialized) {
        analyticsLogger.info('Facebook Pixel', 'Already initialized');
        return;
      }

      // Llamada directa a fbq
      getFbq()('init', pixelId);

      state = {
        ...state,
        initialized: true,
        pixelId,
        lastEventTimestamp: Date.now(),
      };

      analyticsLogger.info('Facebook Pixel', 'Initialized successfully');
    } catch (error) {
      state = {
        ...state,
        lastError: error instanceof Error ? error.message : 'Unknown error',
        lastEventTimestamp: Date.now(),
      };
      analyticsLogger.error('Facebook Pixel', 'Initialization failed', { error });
    }
  },

  pageView: () => {
    try {
      if (!state.initialized) {
        analyticsLogger.warn('Facebook Pixel', 'Not initialized for pageView');
        return;
      }

      // Llamada directa a fbq
      getFbq()('track', 'PageView');
      state.lastEventTimestamp = Date.now();
      analyticsLogger.info('Facebook Pixel', 'Page view tracked');
    } catch (error) {
      state.lastError = error instanceof Error ? error.message : 'Unknown error';
      analyticsLogger.error('Facebook Pixel', 'Page view tracking failed', { error });
    }
  },

  trackEvent: (eventName: string, params?: Record<string, unknown>) => {
    try {
      if (!state.initialized) {
        analyticsLogger.warn('Facebook Pixel', 'Not initialized for event tracking');
        return;
      }

      // Llamada directa a fbq
      getFbq()('track', eventName, params);
      state.lastEventTimestamp = Date.now();
      analyticsLogger.info('Facebook Pixel', `Event tracked: ${eventName}`, params);
    } catch (error) {
      state.lastError = error instanceof Error ? error.message : 'Unknown error';
      analyticsLogger.error('Facebook Pixel', 'Event tracking failed', { error, eventName });
    }
  },

  setConsent: (consent: boolean) => {
    try {
      if (!state.initialized) {
        analyticsLogger.warn('Facebook Pixel', 'Not initialized for consent management');
        return;
      }

      const consentAction = consent ? 'grant' : 'revoke';
      getFbq()('consent', consentAction);

      analyticsLogger.info('Facebook Pixel', `Consent updated: ${consentAction}`);
    } catch (error) {
      state.lastError = error instanceof Error ? error.message : 'Unknown error';
      analyticsLogger.error('Facebook Pixel', 'Consent update failed', { error });
    }
  },
};
